<script setup>
  import { onMounted, reactive, watch } from "vue"
  import api from "../../api/axios.js"
  import $ from "jquery"
  import "datatables.net"
  import "datatables.net-bs5"
  import "datatables.net-bs5/css/dataTables.bootstrap5.css"
  import { useAuth } from "@/composables/useAuth";
  import { destinosDisponibles } from "@/utils/destinos";
  import FechaDatePicker from "@/components/cotizador/FechaDatePicker.vue";
  import { es } from "date-fns/locale";
  const { hasRole } = useAuth();

  // Mismas opciones de origen que ofrece el cotizador al crear la cotización
  // (ver CreateModal.vue/CorpModal.vue), para que el filtro solo permita
  // valores que realmente puedan existir en la BD.
  const origenesFiltro = [
    { value: "BOG", label: "Bogotá" },
    { value: "CLO", label: "Cali" },
    { value: "MDE", label: "Medellín" },
    { value: "CTG", label: "Cartagena" },
    { value: "BAQ", label: "Barranquilla" },
    { value: "BGA", label: "Bucaramanga" },
    { value: "LET", label: "Leticia" },
    { value: "AXM", label: "Armenia" },
    { value: "PEI", label: "Pereira" },
    { value: "PSO", label: "Pasto" },
    { value: "PPN", label: "Popayán" },
    { value: "SMR", label: "Santa Marta" },
    { value: "MTR", label: "Montería" },
    { value: "RCH", label: "Riohacha" },
    { value: "VVC", label: "Villavicencio" },
    { value: "IBE", label: "Ibagué" },
    { value: "NVA", label: "Neiva" },
    { value: "CUC", label: "Cúcuta" },
    { value: "MZL", label: "Manizales" },
    { value: "VUP", label: "Valledupar" },
    { value: "EYP", label: "Yopal" }
  ];

  // Filtros individuales de la tabla: se mandan como query params extra
  // junto a los que ya arma DataTables (draw/start/length/search/order).
  const filtros = reactive({
    origin: "",
    destination: "",
    dateFrom: "",
    dateTo: "",
    passengers: ""
  });

  // Misma lógica que fechaIda/fechaRegreso en el cotizador (useFechas.js):
  // al elegir "desde", "hasta" se autocompleta con el día siguiente, pero
  // sigue siendo editable a mano después.
  watch(() => filtros.dateFrom, (nuevaFecha) => {
    if (!nuevaFecha) return

    const f = new Date(nuevaFecha)
    f.setDate(f.getDate() + 1)
    filtros.dateTo = f.toISOString().split("T")[0]
  })

  let tabla = null;

  function aplicarFiltros() {
    tabla?.ajax.reload();
  }

  function limpiarFiltros() {
    filtros.origin = "";
    filtros.destination = "";
    filtros.dateFrom = "";
    filtros.dateTo = "";
    filtros.passengers = "";
    tabla?.ajax.reload();
  }

  const headers = hasRole("admin")
    ? [
      "ID",
      "Usuario",
      "Fecha creación",
      "Origen",
      "Destino",
      "Fechas",
      "Pasajeros",
      "Teléfono",
      "Precio Desde",
      "PDF"
    ]
    : [
      "ID",
      "Fecha creación",
      "Origen",
      "Destino",
      "Fechas",
      "Pasajeros",
      "Teléfono",
      "Precio Desde",
      "PDF"
    ];

  const getDriveDownloadUrl = (url) => {
    const match = url.match(/\/d\/([^/]+)/)

    if (!match) return url

    const fileId = match[1]
    return `https://drive.google.com/uc?export=download&id=${fileId}`
  }


  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
  }

  const formatCOP = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value)
  }

  onMounted(() => {

    const columns = [
      {
        data: "id",
        defaultContent: ""
      }
    ];

    if (hasRole("admin")) {
      columns.push({
        data: "usuario",
        defaultContent: ""
      });
    }

    columns.push(

      {
        data: "created_at",
        defaultContent: "",
        render: function (data) {
          return data ? formatDate(data) : "";
        }
      },

      {
        data: "origin",
        defaultContent: "",
        render: function (data) {
          return getPlaceName(data);
        }
      },

      {
        data: "destination",
        defaultContent: "",
        render: function (data) {
          return getPlaceName(data);
        }
      },

      {
        data: null,
        orderable: false,
        searchable: false,
        render: function (data, type, row) {

          return `${formatDate(row.start_date)} - ${formatDate(row.end_date)}`;

        }
      },

      {
        data: "passengers",
        defaultContent: 0
      },

      {
        data: "telefono",
        defaultContent: "",
        // El backend manda el teléfono enmascarado (últimos 4 dígitos); el
        // botón pide el número completo bajo demanda vía GET
        // /quotes/:id/telefono, acción que queda auditada en el backend.
        render: function (data, type, row) {
          if (type !== "display") return data || "";
          const valor = data || "";
          return `<span class="telefono-valor">${valor}</span> <button type="button" class="btn btn-sm btn-link p-0 ver-telefono" data-id="${row.id}" title="Ver teléfono completo"><i class="bi bi-eye"></i></button>`;
        }
      },

      {
        data: "total_price",
        defaultContent: 0,
        render: function (data) {
          return formatCOP(data);
        }
      },

      {
        data: "pdf_url",
        orderable: false,
        searchable: false,
        defaultContent: "",
        render: function (data) {

          if (!data) {
            return "";
          }

          return `
          <div class="d-flex gap-2">

            <a href="${data}"
               target="_blank"
               class="btn btn-sm btn-primary">
              Ver PDF
            </a>

            <a href="${getDriveDownloadUrl(data)}"
               class="btn btn-sm btn-dark"
               title="Descargar PDF">

              <i class="bi bi-download"></i>

            </a>

          </div>
        `;
        }
      }

    );

    tabla = $("#quotesTable").DataTable({

      // processing: true,
      serverSide: true,
      stateSave: true,

      pageLength: 50,

      lengthMenu: [50, 75, 100, 150],

      order: [[0, "desc"]],

      ajax: async function (data, callback) {

        try {

          const res = await api.get("/quotes/datatables", {
            params: { ...data, ...filtros }
          });

          callback(res.data);

        } catch (err) {

          console.error(err);

          callback({
            draw: data.draw,
            recordsTotal: 0,
            recordsFiltered: 0,
            data: []
          });

        }

      },

      columns,

      language: {
        decimal: "",
        emptyTable: "No hay datos disponibles en la tabla",
        info: "Mostrando _START_ a _END_ de _TOTAL_ registros",
        infoEmpty: "Mostrando 0 a 0 de 0 registros",
        infoFiltered: "(filtrado de _MAX_ registros totales)",
        lengthMenu: "Mostrar _MENU_ registros",
        loadingRecords: "Cargando...",
        processing: "Procesando...",
        search: "Buscar:",
        zeroRecords: "No se encontraron resultados",
        paginate: {
          first: "<<",
          last: ">>",
          next: ">",
          previous: "<"
        }
      }

    });

    $("#quotesTable").on("click", ".ver-telefono", async function () {
      const $btn = $(this);
      const id = $btn.data("id");
      try {
        const { data } = await api.get(`/quotes/${id}/telefono`);
        $btn.prev(".telefono-valor").text(data.telefono || "");
        $btn.remove();
      } catch (err) {
        console.error(err);
      }
    });

  });


  const placeMap = {
    // ORÍGENES
    BOG: "Bogotá",
    CLO: "Cali",
    MDE: "Medellín",
    CTG: "Cartagena",
    BAQ: "Barranquilla",
    BGA: "Bucaramanga",
    LET: "Leticia",
    AXM: "Armenia",
    PEI: "Pereira",
    PSO: "Pasto",
    PPN: "Popayán",
    SMR: "Santa Marta",
    MTR: "Montería",
    RCH: "Riohacha",
    EYP: "Yopal",
    IBE: "Ibagué",
    NVA: "Neiva",
    CUC: "Cúcuta",
    MZL: "Manizales",
    VVC: "Villavicencio",
    // DESTINOS
    PUJ: "Punta Cana",
    BKK: "Tailandia",
    CUN: "Cancún",
    GIG: "Brasil",
    PERU: "Perú",
    AUA: "Aruba",
    CUR: "Curazao",
    MBJ: "Jamaica",
    PTY: "Panamá (Playa)",
    PTYC: "Ciudad de Panamá",
    VRA: "Varadero",
    HAV: "La Habana",
    PCM: "Playa del Carmen",
    MEX: "Ciudad de México",
    JPN: "Japón",
    "JPN GUIA": "Japón (con guía)",
    SJD: "Los Cabos",
    ADZ: "San Andrés",
    "PTY COMBINADO": "Panamá (combinado)",
    "HAV COMBINADO": "La Habana (combinado)",
    "MEX COMBINADO": "México (combinado)",
    DECAMERON: "Decameron",
  };

  const getPlaceName = (code) => placeMap[code] || code;
</script>


<template>
  <div>

    <div class="card shadow-sm">

      <div class="card-header pb-0">
        <h5 class="mb-0">
          <i class="bi bi-list-ul me-2"></i>
          Cotizaciones
        </h5>
      </div>

      <div class="card-body">

        <div class="filtros-cotizaciones p-3 mb-3">

          <div class="row g-2 g-lg-3 align-items-end">

            <div class="col-12 col-sm-6 col-lg-2">
              <label class="form-label small text-muted mb-1">Origen</label>
              <select v-model="filtros.origin" class="form-select form-select-sm" style="height: 40px;">
                <option value="">Todos</option>
                <option v-for="o in origenesFiltro" :key="o.value" :value="o.value">
                  {{ o.label }}
                </option>
              </select>
            </div>

            <div class="col-12 col-sm-6 col-lg-2">
              <label class="form-label small text-muted mb-1">Destino</label>
              <select v-model="filtros.destination" class="form-select form-select-sm" style="height: 40px;">
                <option value="">Todos</option>
                <option v-for="d in destinosDisponibles" :key="d.value" :value="d.value">
                  {{ d.label }}
                </option>
              </select>
            </div>

            <div class="col-12 col-sm-8 col-lg-3">
              <label class="form-label small text-muted mb-1">Fechas del viaje</label>
              <div class="row g-2">
                <div class="col-6">
                  <FechaDatePicker v-model="filtros.dateFrom" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
                    format="yyyy-MM-dd" :enable-time-picker="false" auto-apply placeholder="Desde" />
                </div>
                <div class="col-6">
                  <FechaDatePicker v-model="filtros.dateTo" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
                    format="yyyy-MM-dd" :enable-time-picker="false" :min-date="filtros.dateFrom || undefined" auto-apply
                    placeholder="Hasta" />
                </div>
              </div>
            </div>

            <div class="col-12 col-sm-4 col-lg-1">
              <label class="form-label small text-muted mb-1">Pasajeros</label>
              <input v-model="filtros.passengers" type="number" min="0" class="form-control form-control-sm"
                style="height: 40px;">
            </div>

            <div class="col-12 col-lg-4">
              <label class="form-label small text-muted mb-1 invisible d-block">Acciones</label>
              <div class="d-flex gap-2" style="
    margin-bottom: -16px;
">
                <button type="button" class="btn btn-sm btn-dark flex-fill text-nowrap" @click="aplicarFiltros"
                  style="height: 40px;">
                  <i class="bi bi-funnel me-1"></i>Filtrar
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary flex-fill text-nowrap"
                  @click="limpiarFiltros" style="height: 40px;">
                  <i class="bi bi-x-lg me-1"></i>Limpiar
                </button>
              </div>
            </div>

          </div>

        </div>

        <div class="table-responsive">

          <table id="quotesTable" class="table table-striped table-bordered align-middle w-100">
            <thead class="table-dark">
              <tr>
                <th v-for="header in headers" :key="header">
                  {{ header }}
                </th>
              </tr>
            </thead>

            <tbody>

            </tbody>
          </table>

        </div>

      </div>

    </div>

  </div>
</template>

<style scoped>
  .filtros-cotizaciones {
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
  }

  /* Los botones de acción deben quedar exactamente a la misma altura que
     los .form-control-sm/.form-select-sm de al lado (Argon les agrega
     box-shadow/hover-transform que en flex-fill los hacía ver más altos). */
  .filtros-cotizaciones .btn-sm {
    height: calc(1.5em + 0.5rem + 2px);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
  }
</style>